import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import SeoSocialTags from '../components/SeoSocialTags';
import '../styles/BlogPrimitives.css';

const BLOG_POST_CANONICAL_URL = 'https://circumvent.io/blog/security-at-machine-speed';
const BLOG_POST_PUBLISHED_AT = '2026-10-26';
/** Lead, headings, body, and close (~710 words). Read time: max(1, round(words / 200 wpm)). */
const BLOG_POST_READ_TIME = '4 min read';
const BLOG_POST_AUTHOR = 'Eric Lee';
const FEATURE_IMAGE_SRC = '/images/blog/security-at-machine-speed.jpg';
const BLOG_POST_OG_IMAGE_URL = 'https://circumvent.io/images/blog/security-at-machine-speed.jpg';
const FEATURE_IMAGE_ALT =
  'A speedometer and a server spilling security findings, across a gap from a magnifying glass over one document and a short chain of cloud, code, and ticket icons.';
const BLOG_POST_OG_TITLE =
  'Security at machine speed: Why finding vulnerabilities is no longer the hard part | Circumvent';
const BLOG_POST_OG_DESCRIPTION =
  'Finding a vulnerability is no longer the hard part. The gap is investigation, ownership, and getting a fix to the right team.';
const BlogSecurityAtMachineSpeedPage = () => (
  <>
    <Helmet>
      <title>{BLOG_POST_OG_TITLE}</title>
      <meta name="description" content={BLOG_POST_OG_DESCRIPTION} />
      <link rel="canonical" href={BLOG_POST_CANONICAL_URL} />
    </Helmet>
    <SeoSocialTags
      title={BLOG_POST_OG_TITLE}
      description={BLOG_POST_OG_DESCRIPTION}
      url={BLOG_POST_CANONICAL_URL}
      ogType="article"
      articlePublishedTime={`${BLOG_POST_PUBLISHED_AT}T00:00:00.000Z`}
      ogImageUrl={BLOG_POST_OG_IMAGE_URL}
    />

    <main className="page page--light blog-post-page">
      <section className="page-section page-section--white" aria-labelledby="post-heading">
        <article className="page-container blog-post">
          <div className="blog-post__header">
            <Link to="/blog" className="blog-crumb-link">
              <span aria-hidden="true">←</span> Blog
            </Link>
            <h1 id="post-heading" className="blog-post__title">
              Security at machine speed
            </h1>
            <p className="blog-post__lead">Why finding vulnerabilities is no longer the hard part</p>

            <ul className="blog-meta-list blog-meta-list--header" aria-label="Post details">
              <li>Perspective</li>
              <li>
                <time dateTime={BLOG_POST_PUBLISHED_AT}>26 Oct 2026</time>
              </li>
              <li>{BLOG_POST_READ_TIME}</li>
              <li>By {BLOG_POST_AUTHOR}</li>
            </ul>
          </div>

          <div className="blog-post__layout">
            <div className="blog-post__content">
              <figure className="blog-post__feature-image blog-post__feature-image--bleed">
                <img
                  src={FEATURE_IMAGE_SRC}
                  alt={FEATURE_IMAGE_ALT}
                  width={1280}
                  height={720}
                  loading="eager"
                  decoding="async"
                />
              </figure>
              <p>
                For most of my career, I&apos;ve watched security teams struggle with the same problem: the time
                between finding a vulnerability and doing something meaningful about it.
              </p>
              <p>
                We invested in better scanners, smarter prioritization and bigger teams. Yet every week, the backlog
                grew.
              </p>
              <p>Now AI is widening the gap.</p>
              <p>
                Engineering teams are shipping code faster, while AI is increasing the pace and potential scale of
                vulnerability discovery. But investigating those findings, understanding their real-world risk and
                getting the right fix to the right team still takes time, often a lot of it.
              </p>
              <p>
                We&apos;re accelerating the discovery of vulnerabilities without accelerating our ability to respond.
              </p>
              <p>
                That&apos;s a problem I&apos;ve seen firsthand, and it&apos;s why I joined Circumvent.
              </p>

              <h2 id="the-bottleneck-isnt-detection">
                The bottleneck isn&apos;t detection. It&apos;s what happens next.
              </h2>
              <p>
                Inside a large bank, a critical finding can trigger an investigation across multiple systems and teams.
              </p>
              <ul>
                <li>Is the workload running in production?</li>
                <li>Is the vulnerable code actually reachable?</li>
                <li>Is the service exposed?</li>
                <li>Who owns the application?</li>
                <li>Has the risk already been assessed?</li>
              </ul>
              <p>
                The answers are scattered across cloud consoles, repositories, asset inventories, risk registers and
                ticketing systems. Sometimes the only person who knows the full context is the engineer who built the
                service.
              </p>
              <p>
                Skilled security engineers spend hours playing detective. And even when a real risk is confirmed,
                tickets can bounce between teams while ownership and remediation or mitigations are worked out.
              </p>
              <p>Meanwhile, the scanners keep producing findings.</p>
              <p>
                Better dashboards and prioritization scores help, but they don&apos;t remove the underlying work. A
                ranked list of 4,000 vulnerabilities is still 4,000 vulnerabilities if each one needs manual
                investigation.
              </p>
              <p>
                You can&apos;t hire your way out of this indefinitely. Asking great engineers to spend more time
                chasing context and tickets isn&apos;t a sustainable answer.
              </p>
              <p>
                As discovery moves toward machine speed, investigation and remediation must catch up.
              </p>

              <h2 id="a-different-approach-to-vulnerability-management">
                A different approach to vulnerability management
              </h2>
              <p>
                That&apos;s what we&apos;re building at Circumvent: agentic security for cloud applications.
              </p>
              <p>
                Rather than simply producing another list of findings, Circumvent uses AI agents to help security
                teams understand their environment, investigate vulnerabilities and drive the right action.
              </p>
              <h3 id="understand">Understand</h3>
              <p>Map cloud resources and code repositories to applications, their context, and controls.</p>
              <h3 id="investigate">Investigate</h3>
              <p>
                Assess whether findings represent actionable risk by examining factors such as exposure, runtime
                context and code reachability.
              </p>
              <h3 id="remediate-and-mitigate">Remediate &amp; Mitigate</h3>
              <p>
                Turn verified findings into actionable work for the right team through tools such as Jira, Slack and
                AI coding assistants, while consolidating duplicate effort.
              </p>
              <p>
                Circumvent works with existing security tools, including Snyk, Tenable, cloud providers, GitHub and
                Bitbucket. The goal isn&apos;t to replace the scanners organizations already use. It&apos;s to close
                the gap between what those tools discover and what security teams can actually resolve.
              </p>

              <h2 id="machine-speed-doesnt-mean-giving-up-control">
                Machine speed doesn&apos;t mean giving up control
              </h2>
              <p>
                My experience in banking has taught me that automation is only valuable if you can trust and explain
                its outcomes.
              </p>
              <p>
                In regulated environments, you need evidence behind decisions, accountability for outcomes and the
                ability to challenge or override a conclusion.
              </p>
              <p>
                That&apos;s why traceability and human oversight matter. Circumvent logs the investigation process so
                teams can review the evidence and reasoning behind decisions. Proposed code changes still go through
                the organization&apos;s normal approval and code-review process.
              </p>
              <p>
                We&apos;re not asking chief information security officers (CISOs) to hand accountability to AI.
                We&apos;re helping their teams automate the investigative work so they can focus their judgment where
                it matters most.
              </p>
              <p>
                The goal isn&apos;t to automate security decisions blindly. It&apos;s to make better-informed
                decisions, faster.
              </p>

              <h2 id="the-next-evolution-of-security-operations">
                The next evolution of security operations
              </h2>
              <p>
                The best security engineers I&apos;ve worked with don&apos;t want to spend their week proving a finding
                doesn&apos;t matter, tracking down application owners or chasing tickets.
              </p>
              <p>They want to focus on the risks that genuinely threaten the business.</p>
              <p>
                As AI accelerates software development and vulnerability discovery, security teams need a way to keep
                pace without sacrificing control. Success won&apos;t simply mean fewer findings on a dashboard. It will
                mean getting from discovery to a defensible decision and the right fix, faster.
              </p>
              <p>That&apos;s the future we&apos;re working toward at Circumvent.</p>

              <div className="blog-post__cta">
                <p>
                  If your vulnerability management process sounds familiar, connect a cloud account and a code
                  repository to see how Circumvent investigates findings in your environment.
                </p>
                <Link to="/#contact" className="btn btn-solid">
                  Request Access
                </Link>
              </div>

              <p>
                Eric Lee
                <br />
                Head of Cyber, Circumvent
              </p>

              <Link to="/blog" className="blog-crumb-link">
                <span aria-hidden="true">←</span> Blog
              </Link>
            </div>

            <aside className="blog-post__sidebar" aria-label="Post navigation">
              <nav className="blog-toc" aria-label="On this page">
                <p className="blog-toc__title">On this page</p>
                <ul className="blog-toc__list">
                  <li className="blog-toc__item">
                    <a className="blog-toc__link" href="#the-bottleneck-isnt-detection">
                      The bottleneck isn&apos;t detection
                    </a>
                  </li>
                  <li className="blog-toc__item">
                    <a className="blog-toc__link" href="#a-different-approach-to-vulnerability-management">
                      A different approach to vulnerability management
                    </a>
                  </li>
                  <li className="blog-toc__item">
                    <a className="blog-toc__link" href="#machine-speed-doesnt-mean-giving-up-control">
                      Machine speed doesn&apos;t mean giving up control
                    </a>
                  </li>
                  <li className="blog-toc__item">
                    <a className="blog-toc__link" href="#the-next-evolution-of-security-operations">
                      The next evolution of security operations
                    </a>
                  </li>
                </ul>
              </nav>
            </aside>
          </div>
        </article>
      </section>
    </main>
  </>
);

export default BlogSecurityAtMachineSpeedPage;
